// src/lib/components/atoms/DateTimePickerInputAtom/DateTimePickerInputAtom.tsx

import React, { forwardRef, useCallback, useEffect, useId, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import CustomDialogAtom from '../CustomDialog/CustomDialogAtom';
import moment from 'moment-timezone';

// ← adjust this path if your helper lives elsewhere:
import { formatDateTime, DateTimeParts } from '@center-platform/spheres/identity';

import DateTimePickerMolecule, {
  DateTimeValue,
  DateTimePickerMoleculeProps,
} from '../../molecules/DateTimePicker/DateTimePickerMolecule';

import './DateTimePickerInputAtom.scss';

export interface DateTimePickerInputAtomProps
  extends Omit<DateTimePickerMoleculeProps, 'value' | 'onChange'> {
  value?: string | null;
  onChange?: (e: React.ChangeEvent<any>) => void;
  onIsoChange?: (iso: string | null) => void;
  name?: string;
  onBlur?: (e: React.FocusEvent<any>) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the readonly display input — used when `label` is empty (no visible `<label>` renders in that case). */
  ariaLabel?: string;
}

const DateTimePickerInputAtom = forwardRef<
  HTMLDivElement,
  DateTimePickerInputAtomProps
>(
  (
    {
      value = null,
      onChange,
      onIsoChange,
      name,
      onBlur,
      label,
      disabled = false,
      className = '',
      dateFormat,
      timezoneOptions,
      defaultTimezone = moment.tz.guess(),
      errors,
      showPreview,
      ariaLabel,
      ...rest
    },
    ref
  ) => {
    const [iso, setIso] = useState<string | null>(value);
    const [open, setOpen] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState<string>(
      defaultTimezone
    );
    const [tempValue, setTempValue] = useState<DateTimeValue | null>(null);
    const inputId = useId();

    useEffect(() => {
      setIso(value ?? null);
    }, [value]);

    const isoToValue = useCallback(
      (iso: string | null): DateTimeValue => {
        const tz = selectedTimezone;
        if (!iso) {
          return { date: null, time: '', timezone: tz };
        }
        const m = moment.utc(iso).tz(tz);
        // A non-ISO-parseable `iso` (bad stored data, a document bound to the wrong property)
        // produces an invalid moment. `m.format('HH:mm')` on it returns the string "Invalid
        // date" (no colon), so downstream `time.split(':').map(Number)` yields a single NaN
        // and `formatDateTime`'s `moment(...).hours(hours).minutes(undefined)` throws — moment
        // treats an `undefined` setter argument as a getter call, breaking the chain. Falling
        // back to "no date" here (same as the `!iso` branch) is the only value every downstream
        // consumer already handles.
        if (!m.isValid()) {
          return { date: null, time: '', timezone: tz };
        }
        return {
          date: new Date(m.year(), m.month(), m.date()),
          time: m.format('HH:mm'),
          timezone: tz,
        };
      },
      [selectedTimezone]
    );

    const valueToIso = useCallback(
      (val: DateTimeValue | null): string | null => {
        if (!val?.date) return null;
        const { date, time, timezone } = val;
        if (timezone && timezone !== selectedTimezone) {
          setSelectedTimezone(timezone);
        }
        const ymd = moment(date).format('YYYY-MM-DD');
        return moment
          .tz(`${ymd} ${time}`, 'YYYY-MM-DD HH:mm', timezone)
          .toISOString();
      },
      [selectedTimezone]
    );

    const setAndBubble = useCallback(
      (nextIso: string | null) => {
        setIso(nextIso);
        if (onChange) {
          onChange({ target: { name, value: nextIso ?? '' } } as any);
        }
        if (onIsoChange) {
          onIsoChange(nextIso);
        }
      },
      [name, onChange, onIsoChange]
    );

    const handleOpenDialog = useCallback(() => {
      setTempValue(isoToValue(iso));
      setOpen(true);
    }, [iso, isoToValue]);

    const handleCancel = useCallback(() => {
      setTempValue(null);
      setOpen(false);
    }, []);

    const handleOk = useCallback(() => {
      if (tempValue) {
        setAndBubble(valueToIso(tempValue));
      }
      setTempValue(null);
      setOpen(false);
    }, [tempValue, setAndBubble, valueToIso]);

    const handlePickerChange = useCallback((val: DateTimeValue) => {
      setTempValue(val);
    }, []);

    const displayValue = isoToValue(iso);
    let pretty = '';
    if (displayValue.date) {
      const parts: DateTimeParts = {
        date: displayValue.date,
        time: displayValue.time,
        timezone: displayValue.timezone,
      };
      pretty = dateFormat
        ? formatDateTime(parts, 'custom', {
            customFormat: dateFormat,
            showTimezone: true,
          })
        : formatDateTime(parts, 'full');
    }

    return (
      <div
        className={['date-time-picker-atom', className].join(' ')}
        ref={ref}
      >
        {label && (
          <label htmlFor={inputId} className="date-time-picker-atom__label">
            {label}
          </label>
        )}

        <fieldset
          className="p-calendar"
          onBlur={onBlur}
        >
          <InputText
            id={inputId}
            value={pretty || 'No date/time selected'}
            readOnly
            disabled={disabled}
            className="date-time-picker-atom__input"
            onClick={() => !disabled && handleOpenDialog()}
            aria-label={label ? undefined : ariaLabel}
          />

          {!disabled && (
            <Button
              type="button"
              icon="pi pi-calendar"
              className="p-button-outlined date-time-picker-atom__btn"
              onClick={handleOpenDialog}
              tabIndex={0}
              aria-label="Open date and time picker"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleOpenDialog();
                }
              }}
            />
          )}
        </fieldset>

        <CustomDialogAtom
          header={label ?? 'Select Date & Time'}
          visible={open}
          onHide={handleCancel}
          className="date-time-picker-atom__dialog"
          style={{ width: 650 }}
          footer={
            <>
              <Button
                label="OK"
                className="p-button Save-button"
                onClick={handleOk}
              />
              <Button
                label="Cancel"
                className="p-button Close-button"
                onClick={handleCancel}
              />
            </>
          }
        >
          <DateTimePickerMolecule
            value={tempValue || isoToValue(iso)}
            onChange={handlePickerChange}
            dateFormat={dateFormat}
            timezoneOptions={timezoneOptions}
            defaultTimezone={defaultTimezone}
            errors={errors as any}
            showPreview={showPreview}
            {...rest}
          />
        </CustomDialogAtom>
      </div>
    );
  }
);

DateTimePickerInputAtom.displayName = 'DateTimePickerInputAtom';
export default DateTimePickerInputAtom;
